import React, { useState, useCallback, useContext } from "react";
import { AuthContext } from "../../context/Auth/AuthContext";
import { toast } from "react-toastify";
import { format, parseISO } from "date-fns";
import {
  Plus, Edit2, Trash2, Wifi, WifiOff, QrCode, Unlink,
  CheckCircle, RefreshCw, Loader2, ChevronDown, ClipboardList, History, Stethoscope, Key
} from "lucide-react";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import api from "../../services/api";
import WhatsAppModal from "../../components/WhatsAppModal";
import MetaConnectionModal from "../../components/MetaConnectionModal";
import MetaDiagnosticsModal from "../../components/MetaDiagnosticsModal";
import WhatsAppCloudTemplateManager from "../../components/WhatsAppCloudTemplateManager";
import ConfirmationModal from "../../components/ConfirmationModal";
import QrcodeModal from "../../components/QrcodeModal";
import PairingCodeModal from "../../components/PairingCodeModal";
import ChannelIcon from "../../components/ChannelIcon";
import { i18n } from "../../translate/i18n";
import { WhatsAppsContext } from "../../context/WhatsApp/WhatsAppsContext";
import toastError from "../../errors/toastError";

// ─── Status badge (WhatsApp-specific states) ─────────────────────────────────

const statusConfig = {
  CONNECTED:    { label: "Conectado",    icon: Wifi,       cls: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300" },
  DISCONNECTED: { label: "Desconectado", icon: WifiOff,    cls: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300" },
  qrcode:       { label: "QR Code",      icon: QrCode,     cls: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300" },
  OPENING:      { label: "Iniciando...", icon: Loader2,    cls: "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300" },
  TIMEOUT:      { label: "Timeout",      icon: Unlink,     cls: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300" },
  PAIRING:      { label: "Emparejando",  icon: QrCode,     cls: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300" },
};

const StatusBadge = ({ whatsApp }) => {
  const channel = whatsApp.channel || "whatsapp";

  // Meta channels show a simpler "Activo / Sin configurar" status
  if (channel !== "whatsapp") {
    const hasToken = !!whatsApp.facebookPageToken || !!whatsApp.facebookPageId;
    if (whatsApp.status === "CONNECTED" || hasToken) {
      return (
        <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300">
          <Wifi className="w-3 h-3" /> Activo
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-semibold bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400">
        Sin configurar
      </span>
    );
  }

  const cfg = statusConfig[whatsApp.status] || statusConfig.DISCONNECTED;
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-semibold ${cfg.cls}`}>
      <Icon className="w-3 h-3" />
      {cfg.label}
    </span>
  );
};

// ─── Add button with dropdown ─────────────────────────────────────────────────

const AddConnectionDropdown = ({ onWhatsApp, onMeta }) => {
  const [open, setOpen] = useState(false);

  return (
    <div className="relative">
      <button
        onClick={() => setOpen(v => !v)}
        className="flex items-center gap-2 px-3 py-2 text-sm font-medium bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors shadow-sm"
      >
        <Plus className="w-4 h-4" />
        Nueva conexión
        <ChevronDown className="w-3.5 h-3.5" />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute right-0 mt-1 w-64 bg-white dark:bg-slate-900 rounded-xl shadow-lg border border-slate-200 dark:border-slate-700 z-20 overflow-hidden">
            {[
              { channel: "whatsapp", label: "WhatsApp" },
              { channel: "whatsapp_cloud", label: "WhatsApp Cloud" },
              { channel: "whatsapp_coexistence", label: "WhatsApp Coexistencia" },
              { channel: "facebook", label: "Facebook Messenger" },
              { channel: "instagram", label: "Instagram DM" }
            ].map(({ channel, label }) => (
              <button
                key={channel}
                onClick={() => {
                  setOpen(false);
                  if (channel === "whatsapp") onWhatsApp();
                  else onMeta(channel);
                }}
                className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors text-left"
              >
                <ChannelIcon channel={channel} size={16} />
                {label}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────

const Connections = () => {
  const { whatsApps, loading } = useContext(WhatsAppsContext);
  const { user } = useContext(AuthContext);
  const isAdmin = user.profile === "admin";

  // WhatsApp modal state
  const [whatsAppModalOpen, setWhatsAppModalOpen] = useState(false);
  const [qrModalOpen, setQrModalOpen] = useState(false);
  const [pairingCodeModalOpen, setPairingCodeModalOpen] = useState(false);
  const [selectedWhatsApp, setSelectedWhatsApp] = useState(null);

  // Meta modal state
  const [metaModalOpen, setMetaModalOpen] = useState(false);
  const [selectedMetaId, setSelectedMetaId] = useState(null);
  const [syncingId, setSyncingId] = useState(null);
  const [metaModalChannel, setMetaModalChannel] = useState("facebook");

  // WhatsApp Cloud template manager state
  const [templateManagerOpen, setTemplateManagerOpen] = useState(false);
  const [templateManagerConnection, setTemplateManagerConnection] = useState(null);

  // Confirm modal
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const initConf = { action: "", title: "", message: "", whatsAppId: "" };
  const [confirmModalInfo, setConfirmModalInfo] = useState(initConf);

  const handleStartWhatsAppSession = async (id) => {
    try { await api.post(`/whatsappsession/${id}`); } catch (err) { toastError(err); }
  };
  const handleRequestNewQrCode = async (id) => {
    try { await api.put(`/whatsappsession/${id}`); } catch (err) { toastError(err); }
  };
  const confirmTitles = {
    disconnect: i18n.t("connections.confirmationModal.disconnectTitle"),
    delete: i18n.t("connections.confirmationModal.deleteTitle"),
    importHistory: i18n.t("connections.confirmationModal.importHistoryTitle"),
  };
  const confirmMessages = {
    disconnect: i18n.t("connections.confirmationModal.disconnectMessage"),
    delete: i18n.t("connections.confirmationModal.deleteMessage"),
    importHistory: i18n.t("connections.confirmationModal.importHistoryMessage"),
  };
  const handleOpenConfirmationModal = (action, whatsAppId) => {
    setConfirmModalInfo({
      action,
      title: confirmTitles[action] || confirmTitles.delete,
      message: confirmMessages[action] || confirmMessages.delete,
      whatsAppId,
    });
    setConfirmModalOpen(true);
  };
  const handleSubmitConfirmationModal = async () => {
    try {
      if (confirmModalInfo.action === "disconnect")
        await api.delete(`/whatsappsession/${confirmModalInfo.whatsAppId}`);
      if (confirmModalInfo.action === "delete") {
        await api.delete(`/whatsapp/${confirmModalInfo.whatsAppId}`);
        toast.success(i18n.t("connections.toasts.deleted"));
      }
      if (confirmModalInfo.action === "importHistory") {
        await api.post(`/whatsappsession/${confirmModalInfo.whatsAppId}/import-history`);
        toast.success(i18n.t("connections.toasts.importStarted"));
      }
    } catch (err) { toastError(err); }
    setConfirmModalInfo(initConf);
  };

  const [diagnosticsConnection, setDiagnosticsConnection] = useState(null);

  const handleOpenMeta = useCallback((id = null, channel = "facebook") => {
    setSelectedMetaId(id);
    setMetaModalChannel(channel);
    setMetaModalOpen(true);
  }, []);

  // Coexistencia: los webhooks `smb_app_state_sync` (agenda) y `history`
  // (180 días de conversaciones) no llegan solos, hay que pedírselos a Meta.
  // Solo se puede una vez por número y dentro de las 24 h del onboarding, así
  // que el botón avisa antes de gastar el único intento.
  const handleCoexistenceSync = async (w) => {
    const ok = window.confirm(
      "Se le pedirá a Meta la agenda de contactos y el historial de conversaciones de este número.\n\n" +
      "Meta solo permite hacerlo UNA vez por número y dentro de las 24 horas siguientes a conectarlo. " +
      "Los mensajes llegan poco a poco, en segundo plano.\n\n¿Continuar?"
    );
    if (!ok) return;

    setSyncingId(w.id);
    try {
      const { data } = await api.post(`/api/whatsapp-cloud/coexistence-sync/${w.id}`);
      toast.success(
        `Sincronización solicitada (${(data.requested || []).join(", ")}). Los mensajes irán apareciendo en unos minutos.`
      );
      if (data.warnings?.length) {
        toast.warn(data.warnings.join(" | "));
      }
    } catch (err) {
      toastError(err);
    } finally {
      setSyncingId(null);
    }
  };

  const handleEditConnection = (w) => {
    const channel = w.channel || "whatsapp";
    if (["facebook", "instagram", "whatsapp_cloud", "whatsapp_coexistence"].includes(channel)) {
      handleOpenMeta(w.id, channel);
    } else {
      setSelectedWhatsApp(w);
      setWhatsAppModalOpen(true);
    }
  };

  const renderActionButtons = (w) => {
    const channel = w.channel || "whatsapp";

    // Meta channels don't have QR / session buttons
    if (["facebook", "instagram", "whatsapp_cloud", "whatsapp_coexistence"].includes(channel)) {
      if (["whatsapp_cloud", "whatsapp_coexistence"].includes(channel)) {
        return (
          <div className="flex flex-wrap items-center justify-center gap-1.5">
            <button
              onClick={() => { setTemplateManagerConnection(w); setTemplateManagerOpen(true); }}
              className="px-2.5 py-1 text-xs font-medium border border-indigo-300 text-indigo-600 dark:text-indigo-400 rounded-lg hover:bg-indigo-50 dark:hover:bg-indigo-950/30 transition-colors flex items-center gap-1"
              title="Gestionar plantillas de WhatsApp"
            >
              <ClipboardList className="w-3 h-3" /> Plantillas
            </button>
            <button
              onClick={() => setDiagnosticsConnection(w)}
              className="px-2.5 py-1 text-xs font-medium border border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-300 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors flex items-center gap-1"
              title="Ver si Meta está entregando los webhooks, si quedó algo sin procesar y si nuestros envíos llegan al cliente"
            >
              <Stethoscope className="w-3 h-3" /> Diagnóstico
            </button>
            <button
              onClick={() => handleEditConnection(w)}
              className="px-2.5 py-1 text-xs font-medium border border-amber-300 dark:border-amber-700 text-amber-700 dark:text-amber-400 rounded-lg hover:bg-amber-50 dark:hover:bg-amber-950/30 transition-colors flex items-center gap-1"
              title="Ver y copiar credenciales completas (Token, Clave Secreta, IDs) para migración"
            >
              <Key className="w-3 h-3" /> Credenciales
            </button>
            {channel === "whatsapp_coexistence" && (
              <button
                onClick={() => handleCoexistenceSync(w)}
                disabled={syncingId === w.id}
                className="px-2.5 py-1 text-xs font-medium border border-purple-300 text-purple-600 dark:text-purple-400 rounded-lg hover:bg-purple-50 dark:hover:bg-purple-950/30 transition-colors flex items-center gap-1 disabled:opacity-50"
                title="Pedirle a Meta la agenda de contactos y el historial de conversaciones del celular (solo se puede una vez por número, dentro de las 24 h de conectarlo)"
              >
                {syncingId === w.id
                  ? <Loader2 className="w-3 h-3 animate-spin" />
                  : <History className="w-3 h-3" />}
                {w.coexistenceSyncStatus || "Sincronizar historial"}
              </button>
            )}
          </div>
        );
      }
      return (
        <div className="flex flex-wrap items-center justify-center gap-1.5">
          <button
            onClick={() => handleEditConnection(w)}
            className="px-2.5 py-1 text-xs font-medium border border-amber-300 dark:border-amber-700 text-amber-700 dark:text-amber-400 rounded-lg hover:bg-amber-50 dark:hover:bg-amber-950/30 transition-colors flex items-center gap-1"
            title="Ver y copiar credenciales completas (Token, Clave Secreta, IDs) para migración"
          >
            <Key className="w-3 h-3" /> Credenciales
          </button>
          <button
            onClick={() => setDiagnosticsConnection(w)}
            className="px-2.5 py-1 text-xs font-medium border border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-300 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors flex items-center gap-1"
            title="Ver si Meta está entregando los webhooks y si quedó algo sin procesar"
          >
            <Stethoscope className="w-3 h-3" /> Diagnóstico
          </button>
        </div>
      );
    }

    return (
      <div className="flex flex-wrap items-center justify-center gap-1.5">
        {w.status === "qrcode" && (
          <>
            <button onClick={() => { setSelectedWhatsApp(w); setQrModalOpen(true); }} className="px-2.5 py-1 text-xs font-medium bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors flex items-center gap-1">
              <QrCode className="w-3 h-3" /> {i18n.t("connections.buttons.qrcode")}
            </button>
            <button onClick={() => { setSelectedWhatsApp(w); setPairingCodeModalOpen(true); }} className="px-2.5 py-1 text-xs font-medium bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors">
              Código
            </button>
          </>
        )}
        {w.status === "DISCONNECTED" && (
          <>
            <button onClick={() => handleStartWhatsAppSession(w.id)} className="px-2.5 py-1 text-xs font-medium border border-indigo-300 text-indigo-600 rounded-lg hover:bg-indigo-50 transition-colors flex items-center gap-1">
              <RefreshCw className="w-3 h-3" /> {i18n.t("connections.buttons.tryAgain")}
            </button>
            <button onClick={() => handleRequestNewQrCode(w.id)} className="px-2.5 py-1 text-xs font-medium border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 rounded-lg hover:bg-slate-50 transition-colors">
              {i18n.t("connections.buttons.newQr")}
            </button>
          </>
        )}
        {w.status === "CONNECTED" && w.engine === "whaileys" && (
          <button
            onClick={() => handleOpenConfirmationModal("importHistory", w.id)}
            className="px-2.5 py-1 text-xs font-medium border border-emerald-300 text-emerald-600 dark:text-emerald-400 rounded-lg hover:bg-emerald-50 dark:hover:bg-emerald-950/30 transition-colors flex items-center gap-1"
            title={i18n.t("connections.buttons.importHistoryTitle")}
          >
            <RefreshCw className="w-3 h-3" /> {i18n.t("connections.buttons.importHistory")}
          </button>
        )}
        {isAdmin && ["CONNECTED", "PAIRING", "TIMEOUT", "OPENING"].includes(w.status) && (
          <button onClick={() => handleOpenConfirmationModal("disconnect", w.id)} className="px-2.5 py-1 text-xs font-medium border border-red-200 text-red-600 rounded-lg hover:bg-red-50 transition-colors" title="Destruye la sesión por completo (solo admin)">
            {i18n.t("connections.buttons.disconnect")}
          </button>
        )}
        {w.status === "OPENING" && (
          <button onClick={() => handleStartWhatsAppSession(w.id)} className="px-2.5 py-1 text-xs font-medium border border-indigo-300 text-indigo-600 rounded-lg hover:bg-indigo-50 transition-colors flex items-center gap-1">
            <RefreshCw className="w-3 h-3" /> {i18n.t("connections.buttons.tryAgain")}
          </button>
        )}
      </div>
    );
  };

  return (
    <MainContainer>
      <ConfirmationModal
        title={confirmModalInfo.title}
        open={confirmModalOpen}
        onClose={setConfirmModalOpen}
        onConfirm={handleSubmitConfirmationModal}
      >
        {confirmModalInfo.message}
      </ConfirmationModal>

      <QrcodeModal
        open={qrModalOpen}
        onClose={useCallback(() => { setSelectedWhatsApp(null); setQrModalOpen(false); }, [])}
        whatsAppId={!whatsAppModalOpen && selectedWhatsApp?.id}
      />
      <PairingCodeModal
        open={pairingCodeModalOpen}
        onClose={useCallback(() => { setSelectedWhatsApp(null); setPairingCodeModalOpen(false); }, [])}
        whatsAppId={!whatsAppModalOpen && !qrModalOpen && selectedWhatsApp?.id}
      />
      <WhatsAppModal
        open={whatsAppModalOpen}
        onClose={useCallback(() => { setWhatsAppModalOpen(false); setSelectedWhatsApp(null); }, [])}
        whatsAppId={!qrModalOpen && selectedWhatsApp?.id}
      />
      <MetaDiagnosticsModal
        open={!!diagnosticsConnection}
        onClose={() => setDiagnosticsConnection(null)}
        connection={diagnosticsConnection}
      />

      <MetaConnectionModal
        open={metaModalOpen}
        onClose={useCallback(() => { setMetaModalOpen(false); setSelectedMetaId(null); }, [])}
        connectionId={selectedMetaId}
        initialChannel={metaModalChannel}
      />
      <WhatsAppCloudTemplateManager
        open={templateManagerOpen}
        onClose={useCallback(() => { setTemplateManagerOpen(false); setTemplateManagerConnection(null); }, [])}
        whatsappId={templateManagerConnection?.id}
        connectionName={templateManagerConnection?.name}
      />

      <MainHeader>
        <Title>{i18n.t("connections.title")}</Title>
        <MainHeaderButtonsWrapper>
          <AddConnectionDropdown
            onWhatsApp={() => { setSelectedWhatsApp(null); setWhatsAppModalOpen(true); }}
            onMeta={(ch) => { setSelectedMetaId(null); handleOpenMeta(null, ch); }}
          />
        </MainHeaderButtonsWrapper>
      </MainHeader>

      <div className="flex-1 overflow-y-auto px-3 sm:px-6 pb-6">
        <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 overflow-x-auto">
          <table className="w-full text-sm min-w-[720px]">
            <thead>
              <tr className="bg-slate-50 dark:bg-slate-800/50 border-b border-slate-200 dark:border-slate-800">
                {["Canal", i18n.t("connections.table.name"), i18n.t("connections.table.status"), i18n.t("connections.table.session"), i18n.t("connections.table.lastUpdate"), i18n.t("connections.table.default"), i18n.t("connections.table.actions")].map((h, i) => (
                  <th key={h} className={`px-4 py-3 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider whitespace-nowrap ${i <= 1 ? "text-left" : "text-center"}`}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {loading ? (
                <tr><td colSpan={7} className="py-8 text-center"><div className="flex justify-center"><div className="w-5 h-5 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" /></div></td></tr>
              ) : whatsApps?.length > 0 ? (
                whatsApps.map((w) => (
                  <tr key={w.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/30 transition-colors">
                    {/* Canal icon */}
                    <td className="px-4 py-3">
                      <ChannelIcon channel={w.channel || "whatsapp"} size={20} />
                    </td>
                    <td className="px-4 py-3 font-medium text-slate-900 dark:text-slate-100">
                      <span className="text-xs text-slate-400 dark:text-slate-500 font-mono mr-1.5 bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5 rounded">ID: {w.id}</span>
                      {w.name}
                    </td>
                    <td className="px-4 py-3 text-center"><StatusBadge whatsApp={w} /></td>
                    <td className="px-4 py-3 text-center">{renderActionButtons(w)}</td>
                    <td className="px-4 py-3 text-center text-slate-500 dark:text-slate-400 text-xs">{format(parseISO(w.updatedAt), "dd/MM/yy HH:mm")}</td>
                    <td className="px-4 py-3 text-center">
                      {w.isDefault && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300">
                          <CheckCircle className="w-3 h-3" /> Por defecto
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-center gap-1">
                        <button
                          onClick={() => handleEditConnection(w)}
                          title="Editar"
                          className="p-1.5 rounded-lg text-indigo-600 hover:bg-indigo-50 dark:hover:bg-indigo-950/30 transition-colors"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        {isAdmin && (
                          <button
                            onClick={() => handleOpenConfirmationModal("delete", w.id)}
                            title="Eliminar (solo admin)"
                            className="p-1.5 rounded-lg text-red-500 hover:bg-red-50 dark:hover:bg-red-950/30 transition-colors"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={7} className="py-16 text-center">
                    <p className="text-slate-500 font-medium">No hay conexiones</p>
                    <p className="text-slate-400 text-xs mt-1">Agrega una conexión de WhatsApp, Facebook o Instagram para comenzar</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </MainContainer>
  );
};

export default Connections;
